import { CHALLENGE_END, CHALLENGE_START, getDisplayToday } from "@/constants/challenge";
import {
  isMissionsBadgePending,
  subscribeDailyProgressChanged,
  toIsoDate,
} from "@/utils/dailyProgress";
import { getTodayCH } from "@/utils/date";
import { supabase } from "@/utils/supabase";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Pastilles « point rouge » de la barre d'onglets, indiquant ce qu'il reste à
 * faire AUJOURD'HUI (masquées une fois fait) :
 *  - challengePending : toutes les actions du jour ne sont pas encore validées
 *  - missionsPending  : quiz du jour non répondu
 *
 * Le serveur force les dates d'écriture au jour réel (getTodayCH). Le quiz du
 * jour suit le calendrier affiché (getDisplayToday, mode simulation admin
 * inclus) via question_id → challenge_date.
 */
export function useDailyBadges() {
  const [challengePending, setChallengePending] = useState(false);
  const [missionsPending, setMissionsPending] = useState(false);
  const refetchSeq = useRef(0);
  // Filet anti-race : un fetch parti AVANT la réponse ne doit pas recoller
  // la pastille. Le flag tient jusqu'au changement de jour affiché.
  const localQuizDoneOn = useRef<string | null>(null);

  const refetch = useCallback(async () => {
    const seq = ++refetchSeq.current;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        if (seq !== refetchSeq.current) return;
        setChallengePending(false);
        setMissionsPending(false);
        return;
      }

      // Hors fenêtre du challenge, rien n'est jouable (actions + quiz verrouillés
      // pour les non-admins). On n'affiche donc aucune pastille : sinon un
      // compte non-admin voit un point rouge permanent « missions » alors que
      // toutes les questions sont bloquées. Les admins en simulation ont un
      // getDisplayToday() ramené dans la fenêtre → pastilles actives comme prévu.
      const displayToday = getDisplayToday();
      if (displayToday < CHALLENGE_START || displayToday > CHALLENGE_END) {
        if (seq !== refetchSeq.current) return;
        setChallengePending(false);
        setMissionsPending(false);
        return;
      }

      const today = getTodayCH();

      const [
        { data: actions },
        { data: spins },
        { data: entries },
        { data: questionsData },
        { data: quiz },
      ] = await Promise.all([
        supabase
          .from("daily_actions")
          .select("action_id")
          .eq("user_id", user.id)
          .eq("action_date", today),
        supabase
          .from("wheel_spins")
          .select("id")
          .eq("user_id", user.id)
          .eq("spin_date", today)
          .limit(1),
        supabase
          .from("contest_entries")
          .select("id")
          .eq("user_id", user.id)
          .eq("entry_date", today)
          .limit(1),
        supabase.rpc("get_quiz_questions"),
        supabase
          .from("quiz_answers")
          .select("question_id, answer_date")
          .eq("user_id", user.id),
      ]);

      if (seq !== refetchSeq.current) return;

      const doneActions = new Set((actions ?? []).map((a) => a.action_id)).size;

      // La roue du jour est « faite » soit par un tour enregistré (wheel_spins),
      // soit par une participation cadeau (contest_entries), comme pour le point
      // de notif de la roue.
      const wheelDone = (spins?.length ?? 0) > 0 || (entries?.length ?? 0) > 0;

      // Point rouge Challenge = il reste quelque chose à faire aujourd'hui. Il
      // disparaît une fois la roue faite ET au moins une action validée (décision
      // produit), et revient le lendemain.
      setChallengePending(!(doneActions >= 1 && wheelDone));

      const questions = Array.isArray(questionsData) ? questionsData : [];
      const todayQuestion = (questions as { id: string; challenge_date: string }[])
        .find((q) => toIsoDate(q.challenge_date) === displayToday);

      const answeredToday = (quiz ?? []).some((a) => toIsoDate(a.answer_date) === today);
      const computedPending = isMissionsBadgePending({
        displayToday,
        challengeStart: CHALLENGE_START,
        challengeEnd: CHALLENGE_END,
        todayQuestionId: todayQuestion?.id,
        answeredQuestionIds: (quiz ?? []).map((a) => a.question_id as string),
        answeredToday,
      });
      const localDone = localQuizDoneOn.current === displayToday;
      setMissionsPending(computedPending && !localDone);
    } catch {
      // non-bloquant : en cas d'erreur, pas de pastille (évite un faux positif).
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Réponse au quiz (même onglet, pas de changement de route) : on éteint la
  // pastille tout de suite, puis on resynchronise. Un fetch plus ancien ne
  // peut plus la rallumer.
  useEffect(() => {
    return subscribeDailyProgressChanged((hint) => {
      if (hint === "quiz") {
        localQuizDoneOn.current = getDisplayToday();
        setMissionsPending(false);
      }
      void refetch();
    });
  }, [refetch]);

  return { challengePending, missionsPending, refetch };
}
